<template>
  <v-dialog
    :value="value"
    max-width="820px"
    persistent
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title class="font-weight-medium secondary--text primary py-3">
        <v-icon class="mr-2" color="secondary" small>mdi-domain</v-icon>
        <span>Billing Profile — {{ partnerName }}</span>
        <v-spacer />
        <v-btn icon x-small :disabled="saving" @click="close">
          <v-icon x-small color="secondary">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="bp-content">
        <v-form ref="form" lazy-validation>
          <section class="bp-section">
            <h4 class="bp-section__title">Settlement Scope</h4>
            <p class="bp-section__hint">
              This decides which collections the working is built on — it is not
              inferred from the partner type.
            </p>

            <v-radio-group
              v-model="form.revenue_scope"
              dense
              hide-details
              class="bp-radio"
            >
              <v-radio value="all_collections">
                <template #label>
                  <span class="bp-radio__label">
                    <strong>All collections</strong>
                    <span>
                      The partner runs the programme for the university and is
                      settled on every student's collection.
                    </span>
                  </span>
                </template>
              </v-radio>
              <v-radio value="sourced_students">
                <template #label>
                  <span class="bp-radio__label">
                    <strong>Only students it sourced</strong>
                    <span>
                      The partner is settled on the students admitted through
                      it.
                    </span>
                  </span>
                </template>
              </v-radio>
            </v-radio-group>

            <v-select
              v-model="form.admission_partner_ids"
              :items="partnerOptions"
              item-text="name"
              item-value="id"
              label="Counts as sourced by this partner"
              hint="Add every partner record this group admits under, so the Admission Service base is not billed short."
              persistent-hint
              multiple
              chips
              small-chips
              deletable-chips
              outlined
              dense
              class="mt-4"
            />
          </section>

          <section class="bp-section">
            <h4 class="bp-section__title">Invoice From</h4>
            <v-row dense>
              <v-col cols="12" md="8">
                <v-text-field
                  v-model.trim="form.legal_name"
                  label="Legal Name*"
                  outlined
                  dense
                  hide-details="auto"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.trim="form.signatory_name"
                  label="Signatory"
                  placeholder="Authority Signatory"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.trim="form.address_line1"
                  label="Address Line 1"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.trim="form.address_line2"
                  label="Address Line 2"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.trim="form.city"
                  label="City"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.trim="form.state"
                  label="State"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.trim="form.pincode"
                  label="Pincode"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="5">
                <v-text-field
                  v-model.trim="form.gstin"
                  label="GSTIN"
                  outlined
                  dense
                  hide-details="auto"
                  :rules="[rules.gstin]"
                  @input="form.gstin = (form.gstin || '').toUpperCase()"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.trim="form.pan"
                  label="PAN"
                  outlined
                  dense
                  hide-details="auto"
                  @input="form.pan = (form.pan || '').toUpperCase()"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model.trim="form.state_code"
                  label="State Code"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
            </v-row>
          </section>

          <section class="bp-section">
            <h4 class="bp-section__title">Bill To</h4>
            <v-row dense>
              <v-col cols="12">
                <v-text-field
                  v-model.trim="form.bill_to_name"
                  label="Name"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.trim="form.bill_to_address_line1"
                  label="Address Line 1"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.trim="form.bill_to_address_line2"
                  label="Address Line 2"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model.trim="form.bill_to_city"
                  label="City"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model.trim="form.bill_to_state"
                  label="State"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model.trim="form.bill_to_pincode"
                  label="Pincode"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.trim="form.bill_to_gstin"
                  label="GSTIN"
                  outlined
                  dense
                  hide-details="auto"
                  @input="
                    form.bill_to_gstin = (
                      form.bill_to_gstin || ''
                    ).toUpperCase()
                  "
                />
              </v-col>
            </v-row>
          </section>

          <section class="bp-section">
            <h4 class="bp-section__title">Bank &amp; Invoice Numbering</h4>
            <v-row dense>
              <v-col cols="12" md="5">
                <v-text-field
                  v-model.trim="form.bank_name"
                  label="Bank Name"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model.trim="form.bank_account_type"
                  label="Account Type"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.trim="form.bank_account_number"
                  label="Account Number"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.trim="form.bank_ifsc"
                  label="IFSC"
                  outlined
                  dense
                  hide-details="auto"
                  @input="form.bank_ifsc = (form.bank_ifsc || '').toUpperCase()"
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model.number="form.invoice_number_padding"
                  label="Digits"
                  type="number"
                  min="1"
                  max="8"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model.number="form.last_invoice_number"
                  label="Last Number Used"
                  type="number"
                  min="0"
                  outlined
                  dense
                  hide-details="auto"
                  :hint="`Next: ${nextNumberPreview}`"
                  persistent-hint
                />
              </v-col>
            </v-row>
          </section>
        </v-form>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-3">
        <v-spacer />
        <v-btn text small :disabled="saving" @click="close">Cancel</v-btn>
        <v-btn small depressed color="primary" :loading="saving" @click="save">
          Save Profile
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
const GSTIN_PATTERN =
  /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

const emptyProfile = () => ({
  legal_name: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  pincode: "",
  gstin: "",
  pan: "",
  state_code: "",
  bill_to_name: "",
  bill_to_address_line1: "",
  bill_to_address_line2: "",
  bill_to_city: "",
  bill_to_state: "",
  bill_to_pincode: "",
  bill_to_gstin: "",
  bank_name: "",
  bank_account_type: "",
  bank_account_number: "",
  bank_ifsc: "",
  invoice_prefix: "",
  invoice_number_padding: 3,
  last_invoice_number: 0,
  revenue_scope: "sourced_students",
  admission_partner_ids: [],
  signatory_name: "",
});

export default {
  name: "BillingProfileDialog",

  props: {
    value: { type: Boolean, default: false },
    partnerId: { type: Number, default: null },
    partnerName: { type: String, default: "" },
    partners: { type: Array, default: () => [] },
  },

  data() {
    return {
      form: emptyProfile(),
      saving: false,
      rules: {
        required: (v) => !!v || "Required",
        gstin: (v) => !v || GSTIN_PATTERN.test(v) || "Not a valid GSTIN",
      },
    };
  },

  computed: {
    partnerOptions() {
      return this.partners;
    },

    nextNumberPreview() {
      const next = Number(this.form.last_invoice_number || 0) + 1;
      const padded = String(next).padStart(
        Number(this.form.invoice_number_padding) || 3,
        "0",
      );
      const prefix = this.form.invoice_prefix
        ? `${this.form.invoice_prefix}/`
        : "";
      return `${prefix}${padded}/${this.financialYearLabel()}`;
    },
  },

  watch: {
    value(opened) {
      if (opened) this.load();
    },
  },

  methods: {
    async load() {
      if (!this.partnerId) return;
      try {
        const res = await this.$axios.get("/revenue_billing_profile", {
          params: { partner_id: this.partnerId },
        });
        this.form = { ...emptyProfile(), ...(res.data.data || {}) };
        this.$nextTick(() => this.$refs.form?.resetValidation());
      } catch (err) {
        this.notifyError(err, "Unable to load the billing profile");
      }
    },

    financialYearLabel() {
      const today = new Date();
      const start =
        today.getMonth() + 1 >= 4
          ? today.getFullYear()
          : today.getFullYear() - 1;
      return `${start}-${start + 1}`;
    },

    async save() {
      if (!this.$refs.form.validate()) {
        this.$store.commit("showSnackbar", {
          message: "Please correct the details and try again",
          color: "error",
        });
        return;
      }

      this.saving = true;
      try {
        await this.$axios.put("/revenue_billing_profile", {
          partner_id: this.partnerId,
          billing_profile: this.form,
        });

        this.$store.commit("showSnackbar", {
          message: "Billing profile saved",
          color: "success",
        });

        this.close();
        this.$emit("saved");
      } catch (err) {
        this.notifyError(err, "Unable to save the billing profile");
      } finally {
        this.saving = false;
      }
    },

    close() {
      this.$emit("input", false);
    },

    notifyError(err, fallback) {
      const data = err?.response?.data;
      const changeset = data?.errors
        ? Object.entries(data.errors)
            .map(
              ([field, messages]) =>
                `${field} ${[].concat(messages).join(", ")}`,
            )
            .join("; ")
        : null;

      this.$store.commit("showSnackbar", {
        message: data?.message || changeset || fallback,
        color: "error",
      });
    },
  },
};
</script>

<style scoped>
.bp-content {
  padding-top: 18px !important;
  max-height: 68vh;
}

.bp-section {
  padding-bottom: 18px;
  margin-bottom: 18px;
  border-bottom: 1px solid #eaecf0;
}

.bp-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.bp-section__title {
  margin: 0 0 2px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #101828;
}

.bp-section__hint {
  margin: 0 0 10px;
  font-size: 12px;
  color: #667085;
}

.bp-radio {
  margin-top: 4px;
}

.bp-radio ::v-deep .v-radio {
  align-items: flex-start;
  margin-bottom: 8px;
}

.bp-radio__label {
  display: flex;
  flex-direction: column;
  font-size: 12.5px;
  color: #475467;
}

.bp-radio__label strong {
  color: #101828;
}
</style>
